import { useState, useEffect, useRef, useCallback } from 'react'
import scrollService from '../services/ScrollService'

/**
 * Hook for tracking visible sections and sticky navigation state
 * @param {string[]} sectionIds - Array of section element IDs to observe
 */
const useSectionObserver = (sectionIds) => {
    const [activeSection, setActiveSection] = useState('')
    const [isNavSticky, setIsNavSticky] = useState(false)
    const observerRef = useRef(null)
    const stickyCleanupRef = useRef(null)
    const lastActiveSectionRef = useRef('')
    const processingRef = useRef(false)
    const rafIdRef = useRef(null)
    const debounceTimeoutRef = useRef(null)
    const isInitializedRef = useRef(false)

    /**
     * Updates the active section state with performance optimization
     * Prevents duplicate updates and uses requestAnimationFrame for smooth rendering
     * @param {string} newSectionId - The ID of the section to set as active
     * @returns {boolean} - True if section was updated, false if no change needed
     */
    const updateActiveSection = useCallback((newSectionId) => {
        if (lastActiveSectionRef.current === newSectionId) {
            return false
        }

        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current)
            rafIdRef.current = null
        }

        processingRef.current = true
        setActiveSection(newSectionId)
        lastActiveSectionRef.current = newSectionId

        rafIdRef.current = requestAnimationFrame(() => {
            processingRef.current = false
        })

        return true
    }, [])

    /**
     * Debounced version of updateActiveSection to prevent rapid state changes
     * Delays section updates by 150ms to avoid excessive re-renders during scrolling
     * @param {string} newSectionId - The ID of the section to set as active after delay
     */
    const debouncedUpdateActiveSection = useCallback(
        (newSectionId) => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
            }

            debounceTimeoutRef.current = setTimeout(() => {
                updateActiveSection(newSectionId)
                debounceTimeoutRef.current = null
            }, 150)
        },
        [updateActiveSection]
    )

    useEffect(() => {
        if (isInitializedRef.current) {
            return
        }

        isInitializedRef.current = true

        if (observerRef.current) {
            observerRef.current.disconnect()
            observerRef.current = null
        }

        /**
         * Sets up the Intersection Observer to track section visibility
         * Creates observer with multiple thresholds for precise visibility detection
         * Calculates which section has the best coverage and proximity to fixed nav
         * @returns {Function} cleanup function to disconnect observer
         */
        const setupObserver = () => {
            const elementsToObserve = sectionIds
                .map((id) => document.getElementById(id))
                .filter(Boolean)

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    const allIntersecting = elementsToObserve.filter((element) => {
                        const rect = element.getBoundingClientRect()
                        return rect.top < window.innerHeight && rect.bottom > 0
                    })

                    if (allIntersecting.length > 0) {
                        const scrollPosition = window.pageYOffset
                        const viewportHeight = window.innerHeight
                        const viewportTop = scrollPosition
                        const viewportBottom = scrollPosition + viewportHeight

                        let bestSection = null
                        let bestCoverage = -1

                        allIntersecting.forEach((element) => {
                            const rect = element.getBoundingClientRect()
                            const elementTop = rect.top + scrollPosition
                            const elementBottom = elementTop + rect.height

                            const visibleTop = Math.max(elementTop, viewportTop)
                            const visibleBottom = Math.min(elementBottom, viewportBottom)
                            const visibleHeight = Math.max(0, visibleBottom - visibleTop)
                            const viewportCoverage = visibleHeight / viewportHeight

                            let finalScore = viewportCoverage

                            // When nav is fixed at top, prioritize sections near the fixed nav
                            if (isNavSticky && viewportCoverage > 0.1) {
                                // Assume fixed nav is approximately 80px tall
                                // Section just below fixed nav (rect.top close to nav height) gets higher score
                                const distanceFromFixedNav = Math.abs(rect.top - 80)
                                const proximityScore = Math.max(
                                    0,
                                    1 - distanceFromFixedNav / viewportHeight
                                )
                                finalScore = viewportCoverage * 0.7 + proximityScore * 0.3
                            }

                            if (finalScore > bestCoverage) {
                                bestCoverage = finalScore
                                bestSection = element
                            }
                        })

                        if (bestSection && bestCoverage > 0.1) {
                            debouncedUpdateActiveSection(bestSection.id)
                        }
                    } else {
                        debouncedUpdateActiveSection('')
                    }
                },
                {
                    threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
                    rootMargin: '0px'
                }
            )

            elementsToObserve.forEach((element) => {
                observerRef.current.observe(element)
            })
        }

        const cleanup = setupObserver()

        return () => {
            isInitializedRef.current = false
            if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
            }
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current)
            }
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
                debounceTimeoutRef.current = null
            }
            if (cleanup) {
                cleanup()
            }
        }
    }, [sectionIds, debouncedUpdateActiveSection, isNavSticky])

    /**
     * Initializes sticky navigation detection by setting up scroll listeners
     * Monitors scroll position to determine when navigation should become sticky
     * @param {HTMLElement} navigationElement - The navigation element to track
     * @returns {Function} cleanup function to remove listeners and cancel initialization
     */
    const initializeStickyDetection = useCallback((navigationElement) => {
        if (!navigationElement) {
            return
        }

        if (stickyCleanupRef.current) {
            stickyCleanupRef.current()
            stickyCleanupRef.current = null
        }

        /**
         * Forces the browser to recalculate layout by accessing offsetHeight
         * This ensures CSS changes are applied before measuring element positions
         * @param {HTMLElement} element - The element to force reflow on
         */
        const forceReflow = (element) => element.offsetHeight

        /**
         * Calculates the original static position of the navigation element
         * Temporarily resets positioning styles to measure where the element
         * would naturally appear in the document flow before sticky positioning
         * @returns {number} The original top position in pixels from document top
         */
        const calculateOriginalPosition = () => {
            const currentPosition = navigationElement.style.position
            const currentTop = navigationElement.style.top

            navigationElement.style.position = 'static'
            navigationElement.style.top = 'auto'
            forceReflow(navigationElement)

            const rect = navigationElement.getBoundingClientRect()
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            const originalPosition = rect.top + scrollTop

            navigationElement.style.position = currentPosition
            navigationElement.style.top = currentTop
            forceReflow(navigationElement)

            return originalPosition
        }

        /**
         * Sets up scroll event listener to track navigation sticky state
         * Calculates when navigation should become sticky and updates state accordingly
         * Uses requestAnimationFrame for performance optimization during scrolling
         * @returns {Function} cleanup function to remove scroll listener and cancel animations
         */
        const setupScrollListener = () => {
            const originalTopPosition = calculateOriginalPosition()
            const stickyTopOffset = parseInt(
                window.getComputedStyle(navigationElement).top || 0
            )
            const stickyActivationPoint = originalTopPosition - stickyTopOffset
            let currentStickyState = false

            /**
             * Updates the sticky navigation state based on current scroll position
             * Compares scroll position to activation point to determine sticky state
             * Only updates state when there's an actual change to prevent unnecessary re-renders
             */
            const updateStickyState = () => {
                const currentScrollPosition =
                    window.pageYOffset || document.documentElement.scrollTop
                const shouldBeSticky = currentScrollPosition >= stickyActivationPoint

                if (shouldBeSticky !== currentStickyState) {
                    currentStickyState = shouldBeSticky
                    setIsNavSticky(shouldBeSticky)
                }
            }

            updateStickyState()

            let rafId
            /**
             * Throttled scroll handler using requestAnimationFrame
             * Prevents excessive scroll event processing for better performance
             * Cancels previous animation frame before requesting new one
             */
            const throttledScrollHandler = () => {
                if (rafId) {
                    cancelAnimationFrame(rafId)
                }
                rafId = requestAnimationFrame(() => {
                    updateStickyState()
                })
            }

            window.addEventListener('scroll', throttledScrollHandler, { passive: true })

            /**
             * Removes scroll event listeners and cancels pending animation frames
             * Ensures proper cleanup to prevent memory leaks and continued event processing
             * @returns {Function} cleanup function reference for external use
             */
            const cleanupScrollListener = () => {
                window.removeEventListener('scroll', throttledScrollHandler)
                if (rafId) {
                    cancelAnimationFrame(rafId)
                }
            }

            stickyCleanupRef.current = cleanupScrollListener
            return cleanupScrollListener
        }

        const rafId = requestAnimationFrame(() => {
            setupScrollListener()
        })

        /**
         * Cancels the initialization process and cleans up any pending operations
         * Prevents memory leaks when component unmounts or navigation element changes
         * @returns {Function} cleanup function for external cancellation
         */
        const cancelInitialization = () => {
            cancelAnimationFrame(rafId)
            if (stickyCleanupRef.current) {
                stickyCleanupRef.current()
                stickyCleanupRef.current = null
            }
        }

        return cancelInitialization
    }, [])

    /**
     * Scrolls the page to the top and resets the active section
     * Clears any pending debounced updates before scrolling
     * Uses scrollService for consistent scroll behavior across the application
     */
    const scrollToTop = () => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
            debounceTimeoutRef.current = null
        }
        updateActiveSection('')
        scrollService.scrollToTop()
    }

    useEffect(() => {
        return () => {
            if (stickyCleanupRef.current) {
                stickyCleanupRef.current()
                stickyCleanupRef.current = null
            }
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current)
            }
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
                debounceTimeoutRef.current = null
            }
        }
    }, [])

    return {
        activeSection,
        isNavSticky,
        scrollToTop,
        initializeStickyDetection
    }
}

export default useSectionObserver
